import { CloudActions, CostCenterServiceUrls } from '@portal/portal-core';
import { call, put, select } from 'redux-saga/effects';
import { get, uniqBy } from 'lodash-es';
import {
  clientProductAccessInviteUsersResult,
  clientProductAccessCancelInvitesResult,
  clientProductAccessInvitesSync,
} from '../actions';
// `../utils` is untyped JS; sendAction/convertToSuccessesAndFailures resolve as `any`.
import { sendAction, convertToSuccessesAndFailures } from '../utils';

// Domain invite shape is a pass-through; only `id` fields are consulted here.
type Invite = Record<string, unknown>;

// Minimal shape of the admin API config threaded through to `sendAction`.
interface AdminClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
  clientStorePath: string;
}

interface InviteUsersAction {
  type: string;
  payload: {
    emailAddresses: string[];
    costCenterId: string;
    productAccessId: string;
  };
}

interface CancelInvitesAction {
  type: string;
  payload: {
    inviteIds: unknown[];
  };
}

interface ResendInvite {
  id: string;
  emailAddress: string;
  costCenterId: string;
  productAccessId: string;
}

interface ResendInvitesAction {
  type: string;
  payload: {
    invites: ResendInvite[];
  };
}

// Wrapped response returned by `sendAction`.
interface InviteResponse {
  succeeded?: boolean;
  failed?: boolean;
  error?: unknown;
  data?: Record<string, unknown>;
}

export const existingInvitesFromClient =
  (clientStorePath: string) =>
    (state: unknown): Invite[] =>
      get(state, `${clientStorePath}invites`) || [];

export function* inviteUsersSaga(
  config: AdminClientConfig,
  action: InviteUsersAction,
  timeout = 10000,
): Generator<unknown, void, unknown> {
  const url = CostCenterServiceUrls.inviteUrl(config.baseApiUrl);
  const serverAction = CloudActions.inviteUsersProductAccess(
    action.payload.emailAddresses,
    action.payload.costCenterId,
    action.payload.productAccessId,
  );
  try {
    const response = (yield call(sendAction, config, serverAction, url, timeout)) as InviteResponse;
    const open = true;
    const { succeeded, failed, data, error } = response;
    if (failed) {
      const success = false;
      yield put(clientProductAccessInviteUsersResult(open, success, {}, error));
    } else if (succeeded) {
      const success = true;
      yield put(clientProductAccessInviteUsersResult(open, success, data, null));
      const { successes } = convertToSuccessesAndFailures(data) as { successes: Invite[] };
      const { clientStorePath } = config;
      const existingInvites = (yield select(
        existingInvitesFromClient(clientStorePath),
      )) as Invite[];
      const updatedInvites = uniqBy(successes.concat(existingInvites), 'id');
      yield put(clientProductAccessInvitesSync(updatedInvites));
    }
  } catch (error) {
    const open = true;
    const success = false;
    yield put(clientProductAccessInviteUsersResult(open, success, {}, error));
  }
}

export function* cancelInvitesSaga(
  config: AdminClientConfig,
  action: CancelInvitesAction,
): Generator<unknown, void, unknown> {
  const url = CostCenterServiceUrls.inviteUrl(config.baseApiUrl);
  const serverAction = CloudActions.inviteUserProductCancelInvites(action.payload.inviteIds);

  try {
    const response = (yield call(sendAction, config, serverAction, url)) as InviteResponse;
    const { succeeded, failed, data, error } = response;
    const open = true;
    if (failed) {
      const successFalse = false;
      yield put(clientProductAccessCancelInvitesResult(open, successFalse, {}, error));
    } else if (succeeded) {
      const success = true;
      yield put(clientProductAccessCancelInvitesResult(open, success, data, null));
      const { successes } = convertToSuccessesAndFailures(data) as { successes: Invite[] };
      const { clientStorePath } = config;
      const existingInvites = (yield select(
        existingInvitesFromClient(clientStorePath),
      )) as Invite[];
      const updatedInvites = uniqBy(successes.concat(existingInvites), 'id');
      yield put(clientProductAccessInvitesSync(updatedInvites));
    }
  } catch (error) {
    const open = true;
    const success = false;
    yield put(clientProductAccessCancelInvitesResult(open, success, {}, error));
  }
}

export function* resendInvitesSaga(
  config: AdminClientConfig,
  action: ResendInvitesAction,
): Generator<unknown, void, unknown> {
  yield call(cancelInvitesSaga, config, {
    ...action,
    payload: {
      inviteIds: action.payload.invites.map((invite) => invite.id),
    },
  });
  yield call(inviteUsersSaga, config, {
    ...action,
    payload: {
      emailAddresses: action.payload.invites.map((invite) => invite.emailAddress),
      costCenterId: action.payload.invites[0].costCenterId,
      productAccessId: action.payload.invites[0].productAccessId,
    },
  });
}
